import template from "./field_polygon_map_static.html";
import en from "./en.json";
import es from "./es.json";
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";

class FieldPolygonMapStaticController {
    /* @ngInject */
    constructor($element, $scope, $q, $timeout) {
        this.$element = $element;
        this.$scope = $scope;
        this.$q = $q;
        this.$timeout = $timeout;
    }

    $onInit() {
        let ctrl = this;
        ctrl.path = [];
        ctrl.pathClosed = false;
        ctrl.pathPolyline = null;
        ctrl.pathPolygon = null;
        ctrl.pointPositionsSignature = "";

        setOptions({
            key: process.env.GOOGLE_PUBLIC_API_KEY,
            v: "weekly",
            libraries: ["maps", "core"],
        });

        let mapsLibrary = importLibrary("maps");
        let coreLibrary = importLibrary("core");

        ctrl.$q.all([mapsLibrary, coreLibrary]).then(function (libs) {
            let [maps, core] = libs;
            ctrl.maps = maps;
            ctrl.core = core;

            ctrl.$timeout(function () {
                ctrl.initializeMap();
            });
        });
    }

    initializeMap() {
        let ctrl = this;
        let mapElement = ctrl.$element.find("div")[0];
        if (!mapElement) {
            return;
        }

        ctrl.map = new ctrl.maps.Map(mapElement, {
            zoom: 15,
            mapTypeId: "hybrid",
            mapId: "DEMO_MAP_ID",
            cameraControl: true,
            zoomControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
            streetViewControl: false,
            controlSize: 20,
        });

        ctrl.pointPositionsWatch = ctrl.$scope.$watchCollection(
            "$ctrl.pointPositions",
            function (newValue) {
                ctrl.syncPathFromInput(newValue || []);
            },
        );

        ctrl.syncPathFromInput(ctrl.pointPositions || []);

        if (ctrl.path.length) {
            ctrl.fitPathBounds();
            return;
        }

        if (ctrl.centerLat !== null && ctrl.centerLat !== undefined) {
            if (ctrl.centerLng !== null && ctrl.centerLng !== undefined) {
                ctrl.map.setCenter(
                    ctrl.normalizeLatLng({
                        lat: ctrl.centerLat,
                        lng: ctrl.centerLng,
                    }),
                );
            }
        }
    }

    syncPathFromInput(pointPositions) {
        let ctrl = this;
        let normalizedPoints = [];

        (pointPositions || []).forEach(function (position) {
            let normalized = ctrl.normalizeLatLng(position);
            if (normalized) {
                normalizedPoints.push(normalized);
            }
        });

        let pathClosed = false;
        if (normalizedPoints.length > 2) {
            let firstPoint = normalizedPoints[0];
            let lastPoint = normalizedPoints[normalizedPoints.length - 1];
            if (ctrl.getDistanceInMeters(firstPoint, lastPoint) <= 1) {
                pathClosed = true;
                normalizedPoints = normalizedPoints.slice(0, -1);
            }
        }

        let nextSignature = ctrl.getSignatureForPath(
            normalizedPoints,
            pathClosed,
        );
        if (nextSignature === ctrl.pointPositionsSignature) {
            return;
        }

        ctrl.path = normalizedPoints;
        ctrl.pathClosed = pathClosed;
        ctrl.pointPositionsSignature = nextSignature;
        ctrl.renderPath();
    }

    normalizeLatLng(position) {
        let ctrl = this;
        let coordinates = ctrl.getPositionCoordinates(position);
        if (!coordinates) {
            return null;
        }

        if (
            typeof position.lat === "function" &&
            typeof position.lng === "function"
        ) {
            return position;
        }

        return new ctrl.core.LatLng(coordinates.lat, coordinates.lng);
    }

    getPositionCoordinates(position) {
        if (position === null || position === undefined) {
            return null;
        }

        let lat;
        let lng;

        if (
            typeof position.lat === "function" &&
            typeof position.lng === "function"
        ) {
            lat = Number(position.lat());
            lng = Number(position.lng());
        } else {
            lat = Number(position.latitude);
            lng = Number(position.longitude);

            if (Number.isNaN(lat) || Number.isNaN(lng)) {
                lat = Number(position.lat);
                lng = Number(position.lng);
            }
        }

        if (Number.isNaN(lat) || Number.isNaN(lng)) {
            return null;
        }

        return {
            lat: lat,
            lng: lng,
        };
    }

    getDistanceInMeters(pointA, pointB) {
        let firstCoordinates = this.getPositionCoordinates(pointA);
        let secondCoordinates = this.getPositionCoordinates(pointB);
        if (!firstCoordinates || !secondCoordinates) {
            return Number.POSITIVE_INFINITY;
        }

        let toRadians = function (degrees) {
            return (degrees * Math.PI) / 180;
        };

        let earthRadiusMeters = 6371000;
        let latitudeDelta = toRadians(
            secondCoordinates.lat - firstCoordinates.lat,
        );
        let longitudeDelta = toRadians(
            secondCoordinates.lng - firstCoordinates.lng,
        );
        let a =
            Math.sin(latitudeDelta / 2) * Math.sin(latitudeDelta / 2) +
            Math.cos(toRadians(firstCoordinates.lat)) *
                Math.cos(toRadians(secondCoordinates.lat)) *
                Math.sin(longitudeDelta / 2) *
                Math.sin(longitudeDelta / 2);
        let c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        return earthRadiusMeters * c;
    }

    getSignatureForPath(path, pathClosed) {
        let values = (path || []).map((position) => {
            let coordinates = this.getPositionCoordinates(position);
            if (!coordinates) {
                return "";
            }

            return [
                coordinates.lat.toFixed(8),
                coordinates.lng.toFixed(8),
            ].join(",");
        });
        return [values.join("|"), String(!!pathClosed)].join("::");
    }

    fitPathBounds() {
        let ctrl = this;
        if (!ctrl.map || !ctrl.path.length) {
            return;
        }

        if (ctrl.path.length === 1) {
            ctrl.map.setCenter(ctrl.path[0]);
            ctrl.map.setZoom(17);
            return;
        }

        let bounds = new ctrl.core.LatLngBounds();
        ctrl.path.forEach(function (position) {
            bounds.extend(position);
        });

        ctrl.map.fitBounds(bounds);
    }

    renderPath() {
        let ctrl = this;
        if (!ctrl.map) {
            return;
        }

        if (ctrl.pathPolyline) {
            ctrl.pathPolyline.setMap(null);
            ctrl.pathPolyline = null;
        }

        if (ctrl.pathPolygon) {
            ctrl.pathPolygon.setMap(null);
            ctrl.pathPolygon = null;
        }

        if (ctrl.path.length >= 2) {
            let polylinePath = ctrl.path;
            if (ctrl.pathClosed && ctrl.path.length >= 3) {
                polylinePath = ctrl.path.concat([ctrl.path[0]]);
            }

            ctrl.pathPolyline = new ctrl.maps.Polyline({
                map: ctrl.map,
                path: polylinePath,
                geodesic: true,
                strokeColor: "#57e140",
                strokeOpacity: 0.9,
                strokeWeight: 2,
            });
        }

        if (ctrl.pathClosed && ctrl.path.length >= 3) {
            ctrl.pathPolygon = new ctrl.maps.Polygon({
                map: ctrl.map,
                paths: ctrl.path,
                strokeColor: "#57e140",
                strokeOpacity: 0.9,
                strokeWeight: 2,
                fillColor: "#57e140",
                fillOpacity: 0.2,
            });
        }

        ctrl.fitPathBounds();
    }

    $onDestroy() {
        let ctrl = this;

        if (ctrl.pointPositionsWatch) {
            ctrl.pointPositionsWatch();
            ctrl.pointPositionsWatch = null;
        }

        if (ctrl.pathPolyline) {
            ctrl.pathPolyline.setMap(null);
            ctrl.pathPolyline = null;
        }

        if (ctrl.pathPolygon) {
            ctrl.pathPolygon.setMap(null);
            ctrl.pathPolygon = null;
        }
    }
}

export default {
    name: "fieldPolygonMapStatic",
    template: template,
    controller: FieldPolygonMapStaticController,
    bindings: {
        pointPositions: "<",
        centerLat: "<?",
        centerLng: "<?",
        heightClass: "@?",
    },
    translations: { en, es },
};
